import { ReactNode } from "react";

import { Receipt, Article } from "@/types/receipt";

import {
  translate,
  formatCurrency,
  formatPercentage,
  getQuantityType,
} from "@/utils/formatters";

import styles from "./FinancialDetails.module.css";

interface FinancialDetailRowProps {
  label: string;
  value: ReactNode;
  smallValue?: boolean;
}

const FinancialDetailRow = ({
  label,
  value,
  smallValue = false,
}: FinancialDetailRowProps) => {
  const valueClasses = [
    styles.value,
    smallValue && styles.valueSmall,
    label && styles.valueWithLabel,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <h5>
      <span className={styles.label}>{label}</span>
      <span className={valueClasses}>{value}</span>
    </h5>
  );
};

interface FinancialDetailsProps {
  article: Article;
  lang: string;
  receipt: Receipt;
}

export const FinancialDetails = ({
  article,
  lang,
  receipt,
}: FinancialDetailsProps) => {
  return (
    <div className={styles.container}>
      {article.art_original_price != null && (
        <FinancialDetailRow
          label={translate(lang, "Originalpris: ", "Original price: ")}
          value={formatCurrency(
            article.art_original_price,
            receipt.total.merchant_currency,
            true
          )}
        />
      )}

      {article.quantity != null && (
        <>
          <FinancialDetailRow
            label={translate(lang, "Antall: ", "Quantity: ")}
            value={`${article.quantity}${getQuantityType(article.quantity_type, lang)}`}
          />
          {article.quantity_original_price != null && (
            <FinancialDetailRow
              smallValue
              label={translate(
                lang,
                "Enhetspris: ",
                "Quantity original price: "
              )}
              value={formatCurrency(
                article.quantity_original_price,
                receipt.total.merchant_currency,
                true
              )}
            />
          )}
        </>
      )}

      {article.discounts?.map((discount, i) => (
        <FinancialDetailRow
          key={i}
          label={translate(lang, "Rabatt: ", "Discount: ")}
          value={
            <>
              {discount.amount && discount.amount > 0
                ? formatCurrency(
                    discount.amount,
                    receipt.total.merchant_currency,
                    true,
                    true
                  )
                : ""}
              {discount.percentage && discount.percentage > 0
                ? ` (${formatPercentage(discount.percentage)})`
                : ""}
            </>
          }
        />
      ))}

      {article.art_price_with_vat != null && (
        <FinancialDetailRow
          label={translate(lang, "Pris inkl. MVA: ", "Price with VAT: ")}
          value={formatCurrency(
            article.art_price_with_vat,
            receipt.total.merchant_currency,
            true
          )}
        />
      )}

      {article.additions?.quantity_recycling_fee != null && article.quantity != null && (
        <FinancialDetailRow
          label={translate(lang, "Pant: ", "Quantity recycling fee: ")}
          value={formatCurrency(
            article.additions.quantity_recycling_fee * article.quantity,
            receipt.total.merchant_currency,
            true
          )}
        />
      )}

      {article.quantity_final_price != null && (
        <FinancialDetailRow
          label={translate(
            lang,
            "Enhet sluttpris: ",
            "Quantity final price: "
          )}
          value={formatCurrency(
            article.quantity_final_price,
            receipt.total.merchant_currency,
            true
          )}
        />
      )}

      {article.art_final_price != null && (
        <FinancialDetailRow
          label={translate(lang, "Sluttpris: ", "Final price: ")}
          value={formatCurrency(
            article.art_final_price,
            receipt.total.merchant_currency,
            true,
            false
          )}
        />
      )}
    </div>
  );
};
